import { createSelector } from '@ngrx/store';
import { pickBy } from 'lodash-es';

import { BetslipType } from '../../core/betslip-type';
import { PickId } from '../../core/picks/pick-id';
import { isBetBuilderPick } from '../../core/utils';
import { betslipPicksListSelector, selectBetslipFlattenedPicksList, selectHasEachWayPick } from '../picks/selectors';
import { filterTypePicks } from '../picks/services/linear-betslip-pick.utils';
import { RewardTokenContext } from '../reward-tokens/reward-tokens.model';
import { selectIsRewardTokensSelectorVisible, selectRewardTokens } from '../reward-tokens/selectors';
import { betslipTypeStateSelector, selectBetBuilderPicksCount } from '../types/selectors';
import {
    selectBetslipTypeErrorsFactory,
    selectComboContainerModuleErrors,
    selectHasComboPreventionForTypeFactory,
    selectSlipErrorsForTypeFactory,
} from '../validation/selectors';

export const comboBetStateSelector = createSelector(betslipTypeStateSelector, (typeState) => typeState.comboBet);

export const selectComboBetIsEachWay = createSelector(comboBetStateSelector, (comboBet) => comboBet.isEachWay);

export const comboBetStateStakeSelector = createSelector(comboBetStateSelector, (comboBet) => ({
    actualStake: comboBet.actualStake,
    stake: comboBet.stake,
}));

export const comboBetPicksSelector = createSelector(comboBetStateSelector, (s) => s.picks);

export const comboBetPickSelectorFactory = (pickId: PickId) =>
    createSelector(comboBetPicksSelector, (picks) => {
        return picks[pickId.toString()];
    });

export const selectComboBetActualStake = createSelector(comboBetStateSelector, (state) => state.actualStake);
export const selectComboBetStake = createSelector(comboBetStateSelector, (state) => state.stake);

export const comboBetSelectedPicksSelector = createSelector(comboBetPicksSelector, (picks) => pickBy(picks, (pick) => pick.isSelected));

export const selectComboBetRewardTokenId = createSelector(comboBetStateSelector, (state) => state.rewardTokenId);

export const selectComboBetRewardToken = createSelector(selectComboBetRewardTokenId, selectRewardTokens, (tokenId, tokens) => {
    return tokenId ? tokens[tokenId] : null;
});

export const selectComboBetPicks = createSelector(comboBetPicksSelector, selectBetslipFlattenedPicksList, (comboBetPicks, picksList) => {
    return picksList.filter((pick) => comboBetPicks[pick.id.toString()]);
});

export const selectComboBetEachWay = createSelector(comboBetStateSelector, (comboState) => comboState.isEachWay);

export const selectComboBetComponentState = createSelector(comboBetStateSelector, selectComboBetPicks, (comboBetState, pickList) => ({
    comboBetState,
    pickList,
}));

export const selectHasBetBuilderModuleErrors = createSelector(
    selectSlipErrorsForTypeFactory(BetslipType.BetBuilder),
    selectBetslipTypeErrorsFactory(BetslipType.BetBuilder),
    selectHasComboPreventionForTypeFactory(BetslipType.BetBuilder),
    (slipErrors, typeErrors, hasComboPrevention) => slipErrors.length > 0 || typeErrors.length > 0 || hasComboPrevention,
);

export const selectPlaceableComboBetPicks = createSelector(comboBetPicksSelector, betslipPicksListSelector, (comboPicks, picksList) =>
    filterTypePicks(comboPicks, picksList, { isSelected: true, isLocked: false }),
);

export const selectPlaceableComboBetBuilderPicksCount = createSelector(
    selectPlaceableComboBetPicks,
    (placeablePicks) => placeablePicks.filter(isBetBuilderPick).length,
);

export const selectMainComboContainerState = (tokenContext: RewardTokenContext) =>
    createSelector(
        comboBetPicksSelector,
        selectComboBetEachWay,
        selectBetBuilderPicksCount,
        selectPlaceableComboBetPicks,
        selectPlaceableComboBetBuilderPicksCount,
        selectHasEachWayPick,
        selectIsRewardTokensSelectorVisible(tokenContext),
        selectComboContainerModuleErrors,
        selectHasComboPreventionForTypeFactory(BetslipType.Combo),
        (
            comboPicks,
            comboEachWay,
            betBuilderPicksCount,
            placeablePicks,
            placeableBetBuilderPicksCount,
            hasEachWayPick,
            isRewardSelectorVisible,
            moduleErrors,
            hasComboPrevention,
        ) => ({
            comboPicks,
            comboEachWay,
            betBuilderPicksCount,
            placeablePicks,
            placeableBetBuilderPicksCount,
            hasEachWayPick,
            isRewardSelectorVisible,
            moduleErrors,
            hasComboPrevention,
        }),
    );
